import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Container, Segment } from 'semantic-ui-react'
import _ from 'lodash'

import { isSignedWithEhr } from '@enlyt/helpers/auth'
import { mergeStyles } from '@enlyt/helpers/mergeStyles'

import useMediaQuery from 'hooks/useMediaQuery'
import LinksActions from 'stores/Links/Actions'
import GlobalActions from 'stores/Global/Actions'
import {
  getExternalLinks,
  getExternalLinksLastUpdated,
  getZoomLinks
} from 'stores/Links/Select'
import useMyHealthPolicyByModel from 'hooks/useMyHealthPolicy'

import { WellnessResources, Welcome } from 'components/Tiles'

import QuickActionBar from 'components/QuickActionBar'
import InformationAtGlance from 'components/InformationAtGlance'

import { useTheme } from 'contexts/ThemeProvider'

import {
  getAccountDetails,
  getDependentDetails,
  getAccountDetailsLoading,
  getDependentDetailsLoading
} from 'stores/Account/Select'
import { user as userSelector } from '@enlyt/stores/Auth/Select'
import AccountActions from 'stores/Account/Actions'
import { useUserLocation } from 'hooks/locations/useUserLocation'
import { UnavailableMessage } from 'containers/MyHealth'
import { items } from '../MyHealth/items.ehr'
import { getCurPatientData } from '@web/stores/Account/Select'
import useHandleRequest from 'hooks/useHandleRequest'
import themeStyles from './styles'

const Space = props => {
  const { spaceSize = 8 } = props
  const style = { height: spaceSize }
  return <div style={style} />
}

const Dashboard = props => {
  const { style = {} } = props
  const { theme } = useTheme()
  const defaultStyles = themeStyles(theme)
  const styles = mergeStyles(defaultStyles, style)
  const dispatch = useDispatch()
  const account = useSelector(getAccountDetails)
  const isAccountDetailsLoading = useSelector(getAccountDetailsLoading)
  const [isEhrSigned, setIsEhrSigned] = useState(false)
  const authenticatedUser = useSelector(userSelector)
  const { isMobile } = useMediaQuery()
  const { i18n } = useTranslation()
  const systemLng = i18n.language || 'en'
  const externalLinks = useSelector(getExternalLinks) || []
  const externalLinksLastUpdated = useSelector(getExternalLinksLastUpdated)
  const zoomLinks = useSelector(getZoomLinks) || []
  const dependentData = useSelector(getDependentDetails)
  const isDependentDetailsLoading = useSelector(getDependentDetailsLoading)
  const patientDetails = useSelector(getCurPatientData)
  const patientId = _.get(patientDetails, 'patientId')
  const isUser = _.get(patientDetails, 'isUser', false)
  const handleRequest = useHandleRequest()

  useEffect(
    () => {
      const checkEhrSigned = async () => {
        const isEhrSigned = await isSignedWithEhr(authenticatedUser?.idToken)
        setIsEhrSigned(isEhrSigned)
      }
      checkEhrSigned()
      if (
        _.isEmpty(externalLinks) ||
        !externalLinksLastUpdated ||
        Date.now() - externalLinksLastUpdated > 6000000
      ) {
        dispatch(LinksActions.getExternalLinks(systemLng))
      }
      if (_.isEmpty(zoomLinks)) {
        dispatch(LinksActions.getZoomLinks())
      }
      dispatch(GlobalActions.getGlobalPolicy())
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authenticatedUser]
  )

  useEffect(
    () => {
      if (
        !isAccountDetailsLoading &&
        !_.isEmpty(authenticatedUser) &&
        _.isEmpty(account)
      ) {
        dispatch(AccountActions.getAccountDetails())
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authenticatedUser]
  )

  useEffect(
    () => {
      if (!isDependentDetailsLoading && isEhrSigned && !_.isEmpty(account)) {
        if (_.isEmpty(dependentData)) {
          dispatch(AccountActions.getDependentDetails())
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEhrSigned, account]
  )
  useUserLocation()

  const itemStates = items.map(item => ({
    // eslint-disable-next-line react-hooks/rules-of-hooks
    loading: useSelector(item.selectors.loading),
    // eslint-disable-next-line react-hooks/rules-of-hooks
    json: useSelector(item.selectors.json),
    request: item.request,
    queryFHIR: item.queryFHIR,
    name: item.name,
    // eslint-disable-next-line react-hooks/rules-of-hooks
    isAccessable: useMyHealthPolicyByModel(item.name)
  }))

  useEffect(
    () => {
      itemStates.forEach(item => handleRequest(item))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patientId, isUser]
  )

  const [isAdolescent, setIsAdolescent] = useState(false)
  return (
    <Segment vertical style={styles.container}>
      <Welcome name={account?.name || ''} />
      {isAdolescent && <UnavailableMessage style={styles.unavailableMessage} />}
      <Container>
        <Space />
        <Space />
        <QuickActionBar />
        {!isMobile && <Space />}
        <InformationAtGlance setAdolescent={setIsAdolescent} />
        {!isMobile && <Space />}
      </Container>
      <WellnessResources />
    </Segment>
  )
}

export default Dashboard
